import { Link } from "wouter";
import {
  ArrowRight,
  Check,
  MessageSquare,
  ClipboardList,
  FileCheck2,
  CircleDollarSign,
} from "lucide-react";
import "./marketing.css";
export const cases = [
  {
    name: "Desert Moon Lighting",
    screen: "/images/work/desert-moon-quote.png",
    tag: "Home services",
    title: "From a customer conversation to a clear quote.",
    body: "Customer records, measurements, proposals, agreements, and payment steps organized around the sales team’s workflow.",
    flow: ["Customer & measurements", "Quote & agreement", "Payment steps"],
    status: "Web system + native iPhone app",
  },
  {
    name: "Soil Seed & Water",
    tag: "Wholesale & yard operations",
    title: "Keep sales and operations on the same page.",
    body: "Customer, order, invoice, and payment workflows for the office and mobile sales team. The wider SSW World rollout is continuing in stages.",
    flow: ["Customer & order", "Invoice & payment", "Team visibility"],
    status: "Employee web + iOS and Android workflows",
  },
  {
    name: "Testal Mexican Kitchen",
    tag: "Restaurants",
    title: "Make ordering easier for guests and the team.",
    body: "Direct ordering and restaurant workflow development, with payment and operational integrations scoped around the kitchen. New Square and courier work is in progress.",
    flow: ["Menu & ordering", "Payment workflow", "Kitchen handoff"],
    status: "Ordering systems · integrations in development",
  },
  {
    name: "CompostDeveloper",
    screen: "/images/work/compost-field.png",
    tag: "Compost & soil producers",
    title: "Bring field records into the office.",
    body: "Pile and windrow records, recipes, temperatures, turns, and photos in one workflow. Field app pilots extend the same records to the crew’s phones.",
    flow: ["Piles & recipes", "Field records", "Office review"],
    status: "Web platform + field app pilot",
  },
];
export function Action({
  children = "Find my AI opportunities",
  href = "/ai-sales",
  secondary = false,
}: {
  children?: React.ReactNode;
  href?: string;
  secondary?: boolean;
}) {
  return (
    <Link href={href} className={`btn${secondary ? " secondary" : ""}`}>
      {children}
      <ArrowRight size={16} />
    </Link>
  );
}
export function Workflow() {
  return (
    <div className="workflow">
      <div className="workflowtop">
        <span>Less chasing. More moving.</span>
        <i>A connected workflow</i>
      </div>
      {[
        [
          MessageSquare,
          "A new inquiry comes in",
          "Captured with a clear next step",
        ],
        [
          ClipboardList,
          "The right person takes over",
          "Context, reminders, and follow-up",
        ],
        [
          FileCheck2,
          "The work moves forward",
          "Quotes, approvals, and team updates",
        ],
        [
          CircleDollarSign,
          "The details stay connected",
          "Invoices, payments, and customer history",
        ],
      ].map(([Icon, title, body]) => {
        const I = Icon as typeof Check;
        return (
          <div className="flowrow" key={title as string}>
            <div className="flowicon">
              <I size={18} />
            </div>
            <div>
              <strong>{title as string}</strong>
              <span>{body as string}</span>
            </div>
            <Check className="check" size={15} />
          </div>
        );
      })}
      <div className="ownerstrip">
        <strong>You see what needs attention.</strong>Your team keeps work
        moving. You stay in control.
      </div>
      <p className="flowcaption">
        Illustrative workflow. Each system is scoped to your business.
      </p>
    </div>
  );
}
export function CaseStudies() {
  return (
    <div className="workgrid">
      {cases.map((c) => (
        <article className="workcard" key={c.name}>
          {c.screen ? (
            <div className="product-proof">
              <div className="proof-steps">
                <span className="eyebrow">Inside the system</span>
                {c.flow.map((step, i) => (
                  <div key={step}>
                    <span>{String(i + 1).padStart(2, "0")}</span>
                    <strong>{step}</strong>
                  </div>
                ))}
                <p>
                  Actual app screens.
                  <br />
                  Demonstration data.
                </p>
              </div>
              <div className="proof-phone">
                <img
                  src={c.screen}
                  alt={`${c.name}: ${c.name === "CompostDeveloper" ? "windrow temperature records on mobile" : "customer quote and measurements on mobile"}`}
                  loading="lazy"
                  width={430}
                  height={880}
                />
              </div>
            </div>
          ) : (
            <div className="workvisual" aria-label={`${c.name} workflow`}>
              {c.flow.map((step, i) => (
                <span key={step}>
                  {i === 0 ? <ClipboardList size={20} /> : i === 1 ? <FileCheck2 size={20} /> : <Check size={20} />}
                  <strong>{step}</strong>
                  {i < 2 && <ArrowRight size={16} />}
                </span>
              ))}
            </div>
          )}
          <div className="workbody">
            <span className="eyebrow">{c.tag}</span>
            <h3>{c.title}</h3>
            <p>{c.body}</p>
            <strong style={{ fontSize: 14 }}>{c.name}</strong>
            <div className="small" style={{ marginTop: 5 }}>
              {c.status}
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}
export function FreePlanCTA() {
  return (
    <section className="section dark">
      <div className="wrap split">
        <div>
          <span className="eyebrow">A useful place to start</span>
          <h2>Find the work your business can stop doing by hand.</h2>
          <p className="intro">
            Answer four workflow questions. Get three practical improvements, a
            first step for each, and what to measure.
          </p>
        </div>
        <div>
          <h3>Your free AI Opportunity Plan</h3>
          <ul className="clean" style={{ marginBottom: 24 }}>
            <li>Based on how your business works today</li>
            <li>Actions you can try before buying software</li>
            <li>Optional call if you want help building it</li>
          </ul>
          <Action>Get my free plan</Action>
          <p className="small" style={{ marginTop: 14 }}>
            About 2 minutes · No credit card · No obligation
          </p>
        </div>
      </div>
    </section>
  );
}
